import { AppIcon } from '@/components/ui/app-icon';
import {
  AgentCard,
  CARD_LINK,
} from '@/components/agent-chat/agent-card.component';
import { OpenRunLink } from '@/components/agent-chat/agent-cards.component';
import {
  CardCost,
  CardFooter,
  CardFree,
  CardMeta,
  CardRow,
  CardRows,
  CardShortOfCredits,
  CardStatus,
  CardText,
} from '@/components/agent-chat/card-parts.component';
import { ReviewCardChangeList } from '@/containers/agents/review-card.component';
import { useCardBalance } from '@/components/agent-chat/use-buy-credits.hook';
import { ListRowsLink } from '@/components/agent-chat/list-rows-link.component';
import { CONSENT_SPLIT_FLAG } from '@/containers/agents/use-review-card.hook';
import {
  isRunLive,
  isRunSkipped,
  RUN_LABEL,
} from '@/containers/agents/run-status.util';
import {
  connectLabel,
  serverReason,
} from '@/containers/agents/server-reason.util';
import { useAgentsAccess } from '@/hooks/use-agents-access.hook';
import type { ConfirmationRequest } from '@/hooks/use-agent-chat.hook';
import {
  iconArrowsRotate,
  iconCircleCheck,
  iconPlay,
  iconRobot,
  iconSitemap,
} from '@/lib/icons';
import { useAppDispatch } from '@/store';
import {
  advancedPlaybooksApi,
  useGetAdvancedRunQuery,
} from '@/store/features/custom-playbooks.feature';
import {
  gtmAgentsApi,
  useSetAgentStatusMutation,
} from '@/store/features/gtm-agents.feature';
import { sendAiChatMessage, setAiChatOpen } from '@/store/features/ui.feature';
import type { RunStatus } from '@/types/custom-playbooks';
import { Link, useNavigate } from '@tanstack/react-router';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  draftAgentOf,
  oneOffCardView,
  oneOffTitle,
  repeatAgentOf,
  reviewResultsMessage,
  runStepsView,
  startedRunOf,
  type OneOffRow,
} from './one-off.util';

type ConfirmResult = { ok: boolean; aborted?: boolean } | void;

function fromHistory(result: unknown): boolean {
  return (
    !!result &&
    typeof result === 'object' &&
    (result as { __fromHistory?: unknown }).__fromHistory === true
  );
}

/** A confirmation row: words, a price, or Free at the right. The words show
 * in full: they are what Confirm agrees to (a brief, what a write changes). */
function OneOffCardRow({ row }: { row: OneOffRow }) {
  const cost =
    row.cost === 'free' ? (
      <CardFree />
    ) : row.cost ? (
      <CardCost
        amount={row.cost.amount}
        upTo={row.cost.upTo}
        unit={row.cost.unit}
      />
    ) : null;
  return (
    <CardRow
      label={row.label}
      wrap
      end={
        row.value && cost ? (
          <span className='flex items-center gap-[8px]'>
            <span>{row.value}</span>
            {cost}
          </span>
        ) : (
          (row.value ?? cost)
        )
      }
    />
  );
}

// ---------------------------------------------------------------------------
// The confirmation (run_one_off_play, run_agent_now, repeat_one_off,
// create_draft_agent)
// ---------------------------------------------------------------------------

/**
 * A one-off's card in the agent card shell: what it does in the header, the
 * most it can spend at the right; what it runs, each step with its price and
 * each write; then Cancel and the action's verb. When the balance can't
 * cover the most, the main button is Buy credits. The main button locks both
 * until the action answers and unlocks them when it fails.
 */
export function OneOffConfirmationCard({
  request,
  onConfirm,
  onReject,
  secondsLeft,
  countdown,
}: {
  request: ConfirmationRequest;
  onConfirm: () => void | Promise<ConfirmResult>;
  onReject: () => void;
  secondsLeft: number | null;
  countdown?: ReactNode;
}) {
  const consentSplit = useFeatureFlagEnabled(CONSENT_SPLIT_FLAG) === true;
  const view = oneOffCardView(
    request as unknown as { tool_name?: string } & Record<string, unknown>
  );
  const expired = secondsLeft !== null && secondsLeft <= 0;
  const icon =
    request.tool_name === 'run_agent_now'
      ? iconPlay
      : request.tool_name === 'repeat_one_off'
        ? iconArrowsRotate
        : request.tool_name === 'create_draft_agent'
          ? iconRobot
          : iconSitemap;
  const total = view?.total;
  const balance = useCardBalance(view?.balance);
  const short = !!total && balance !== null && total.amount > balance;
  const reviewRows = consentSplit ? view?.reviewRows : undefined;
  const trailing =
    !expired && (total || countdown) ? (
      <span className='flex items-center gap-[8px]'>
        {total ? (
          <CardCost
            amount={total.amount}
            upTo={total.upTo}
            unit={total.unit}
            short={short}
          />
        ) : null}
        {countdown}
      </span>
    ) : undefined;

  return (
    <AgentCard
      icon={icon}
      title={
        expired
          ? 'Action expired'
          : (view?.title ?? oneOffTitle(request.tool_name))
      }
      data-one-off-confirmation
      trailing={trailing}
    >
      {expired ? (
        <CardText>This action expired. Please ask again.</CardText>
      ) : (
        <>
          <div className='flex flex-col gap-[8px]'>
            <div className='flex flex-col gap-[4px]'>
              <CardText lines={0}>{view?.text ?? request.description}</CardText>
              {view?.meta ? <CardMeta>{view.meta}</CardMeta> : null}
            </div>
            {view?.rows.length ? (
              <CardRows>
                {view.rows.map((row, index) => (
                  <OneOffCardRow
                    key={`${index}-${row.label}-${row.value ?? ''}`}
                    row={row}
                  />
                ))}
              </CardRows>
            ) : null}
            {reviewRows?.length ? (
              <div data-one-off-review>
                <CardRows>
                  {reviewRows.map((row, index) => (
                    <Fragment key={`${index}-${row.label}-${row.value ?? ''}`}>
                      <OneOffCardRow row={row} />
                      {row.changes ? (
                        <ReviewCardChangeList
                          label={row.label}
                          changes={row.changes}
                        />
                      ) : null}
                    </Fragment>
                  ))}
                </CardRows>
              </div>
            ) : null}
            {short && balance !== null ? (
              <CardShortOfCredits have={balance} />
            ) : null}
          </div>
          <CardFooter
            cancel={{ onClick: onReject }}
            primary={{
              label: view?.verb ?? 'Confirm',
              workingLabel: view?.working ?? 'Confirming',
              onClick: () => onConfirm(),
            }}
            short={short && total ? { needed: total.amount } : undefined}
          />
        </>
      )}
    </AgentCard>
  );
}

// ---------------------------------------------------------------------------
// The run a confirmed card started (one_off_run_started, agent_run_started)
// ---------------------------------------------------------------------------

/** A run's status as the header's status reads it. */
function runTone(
  status: RunStatus
): 'success' | 'warning' | 'danger' | undefined {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'danger';
  if (status === 'cancelled') return undefined;
  return 'warning';
}

/**
 * A run's live card: its status, each step with how many of its records it
 * has finished, the credits used, and where to open it (the run, the list its
 * leads are in). A one-off offers Review results once it has finished, which
 * asks Clu to read the run back. It polls the run every 3 seconds while it
 * is live. The links need the agents surface, so they stay hidden until
 * Agents is unlocked.
 */
export function OneOffRunCard({
  displayType,
  display,
  result,
}: {
  displayType: string;
  display: unknown;
  result: unknown;
}) {
  const dispatch = useAppDispatch();
  const { unlocked } = useAgentsAccess();
  const started = startedRunOf(displayType, display, result);
  const [live, setLive] = useState(true);
  const { data: run, isError } = useGetAdvancedRunQuery(started?.runId ?? '', {
    skip: !started || !unlocked,
    pollingInterval: live ? 3000 : 0,
  });
  const sentRef = useRef(false);
  const [sent, setSent] = useState(false);
  const refreshed = useRef(false);

  // A run that can't be read (gone, or not this user's) stops the polling.
  useEffect(() => {
    if (isError) setLive(false);
    else if (run) setLive(isRunLive(run.status));
  }, [run, isError]);

  // A live result refreshes the runs lists once.
  useEffect(() => {
    if (!started || refreshed.current || fromHistory(result)) return;
    refreshed.current = true;
    dispatch(
      advancedPlaybooksApi.util.invalidateTags([
        { type: 'CustomRun', id: 'LIST' },
      ])
    );
    dispatch(
      gtmAgentsApi.util.invalidateTags([{ type: 'CustomRun', id: 'LIST' }])
    );
  }, [dispatch, result, started]);

  if (!started) return null;
  const status = run?.status;
  const skipped = run ? isRunSkipped(run) : false;
  const steps = run ? runStepsView(run) : [];
  const listId = run?.leadListId ?? started.listId;
  const finished = !!status && !isRunLive(status);
  const runLink =
    started.kind === 'agent' ? (
      <span className='text-[12px] leading-[16px]'>
        <OpenRunLink runId={started.runId} agentId={started.agentId} />
      </span>
    ) : started.playId ? (
      <Link
        to='/agents/plays/$playId'
        params={{ playId: started.playId }}
        search={{ run: started.runId }}
        className={CARD_LINK}
      >
        Open run
      </Link>
    ) : null;
  const review = started.kind === 'one_off' && finished;

  return (
    <AgentCard
      icon={started.kind === 'agent' ? iconPlay : iconSitemap}
      title={started.kind === 'agent' ? 'Live run' : 'Run once'}
      data-one-off-run
      trailing={
        status ? (
          <CardStatus tone={skipped ? undefined : runTone(status)}>
            {skipped ? 'Skipped' : (RUN_LABEL[status] ?? status)}
          </CardStatus>
        ) : undefined
      }
    >
      {steps.length ? (
        <div className='flex flex-col gap-[8px]'>
          <CardRows>
            {steps.map((step) => (
              <CardRow
                key={step.id}
                label={
                  <>
                    {step.done ? (
                      <AppIcon
                        icon={iconCircleCheck}
                        className='mr-[6px]'
                        style={{ fontSize: 11 }}
                      />
                    ) : null}
                    {step.name}
                  </>
                }
                end={<span className='tabular-nums'>{step.progress}</span>}
              />
            ))}
          </CardRows>
          <CardMeta>
            <span className='inline-flex items-center gap-[4px]'>
              Used <CardCost amount={run?.creditsUsed ?? 0} />
            </span>
          </CardMeta>
        </div>
      ) : (
        <CardText>Starting</CardText>
      )}
      {unlocked && (runLink || listId || review) ? (
        <CardFooter
          links={
            runLink || listId ? (
              <>
                {runLink}
                {listId ? (
                  <ListRowsLink listId={listId} className={CARD_LINK}>
                    Open list
                  </ListRowsLink>
                ) : null}
              </>
            ) : undefined
          }
          primary={
            review
              ? {
                  label: 'Review results',
                  disabled: sent,
                  onClick: () => {
                    if (sentRef.current) return false;
                    sentRef.current = true;
                    setSent(true);
                    dispatch(
                      sendAiChatMessage(reviewResultsMessage(started.runId))
                    );
                    // Sent once: the button stays off, with no spinner, as
                    // Clu's reply shows the reading.
                    return false;
                  },
                }
              : undefined
          }
        />
      ) : null}
    </AgentCard>
  );
}

// ---------------------------------------------------------------------------
// The agent a repeat made (repeat_agent_created)
// ---------------------------------------------------------------------------

/**
 * The agent a one-off became: its name, off until turned on, with Open agent
 * and Turn on (the Agent page's status change). A refused Turn on says the
 * server's reason, with a Connect link when the agent's play uses a connector
 * that isn't connected yet. Hidden until Agents is unlocked.
 */
export function RepeatAgentCard({
  display,
  result,
}: {
  display: unknown;
  result: unknown;
}) {
  const dispatch = useAppDispatch();
  const { unlocked } = useAgentsAccess();
  const agent = repeatAgentOf(display, result);
  const [setStatus] = useSetAgentStatusMutation();
  const [state, setState] = useState<'off' | 'on' | 'failed'>('off');
  // The server's reason Turn on was refused, and the Connect link when it
  // was a connector the agent's play uses that isn't connected yet.
  const [refusal, setRefusal] = useState<{
    reason: string;
    connect: string | null;
  } | null>(null);
  const refreshed = useRef(false);

  // A live result shows the new agent in the Agents list at once.
  useEffect(() => {
    if (!agent || refreshed.current || fromHistory(result)) return;
    refreshed.current = true;
    dispatch(
      gtmAgentsApi.util.invalidateTags([{ type: 'GtmAgent', id: 'LIST' }])
    );
  }, [agent, dispatch, result]);

  if (!agent || !unlocked) return null;

  return (
    <AgentCard
      icon={iconArrowsRotate}
      title={agent.name}
      data-repeat-agent
      trailing={
        <CardStatus tone={state === 'on' ? 'success' : undefined}>
          {state === 'on' ? 'On' : 'Off'}
        </CardStatus>
      }
    >
      {state === 'failed' ? (
        <CardMeta tone='warning'>
          {refusal?.reason ?? "Couldn't turn on"}
        </CardMeta>
      ) : null}
      <CardFooter
        links={
          <>
            <Link
              to='/agents/$agentId'
              params={{ agentId: agent.agentId }}
              className={CARD_LINK}
            >
              Open agent
            </Link>
            {state === 'failed' && refusal?.connect ? (
              <Link to='/integrations' className={CARD_LINK}>
                {refusal.connect}
              </Link>
            ) : null}
          </>
        }
        primary={
          state === 'on'
            ? undefined
            : {
                label: 'Turn on',
                workingLabel: 'Turning on',
                onClick: () =>
                  setStatus({ id: agent.agentId, body: { status: 'active' } })
                    .unwrap()
                    .then(() => {
                      setRefusal(null);
                      setState('on');
                      return true;
                    })
                    .catch((error: unknown) => {
                      setRefusal({
                        reason: serverReason(error, "Couldn't turn on"),
                        connect: connectLabel(error),
                      });
                      setState('failed');
                      // Unlocks Turn on for another try.
                      return false;
                    }),
              }
        }
      />
    </AgentCard>
  );
}

// ---------------------------------------------------------------------------
// The draft agent a chat made (draft_agent_created)
// ---------------------------------------------------------------------------

// The draft agents this tab already opened: a re-render or a remount of the
// card never opens one twice, and Back from the agent stays in the chat.
const openedDraftAgents = new Set<string>();

/**
 * The draft agent made for work only an agent runs, in one line: its name,
 * a draft, and Open. Its play is being built in the agent's own chat, which
 * a live result opens, with the chat docked. Hidden until Agents is
 * unlocked.
 */
export function DraftAgentCard({
  display,
  result,
}: {
  display: unknown;
  result: unknown;
}) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { unlocked } = useAgentsAccess();
  const agent = draftAgentOf(display, result);
  const agentId = agent?.agentId;
  const chatId = agent?.chatId;
  const live = !fromHistory(result);
  const refreshed = useRef(false);

  // A live result shows the new agent in the Agents list at once.
  useEffect(() => {
    if (!agentId || refreshed.current || !live) return;
    refreshed.current = true;
    dispatch(
      gtmAgentsApi.util.invalidateTags([{ type: 'GtmAgent', id: 'LIST' }])
    );
  }, [agentId, dispatch, live]);

  // A live result opens the agent on its build, once.
  useEffect(() => {
    if (!agentId || !live || !unlocked || openedDraftAgents.has(agentId))
      return;
    openedDraftAgents.add(agentId);
    void navigate({
      to: '/agents/$agentId',
      params: { agentId },
      search: chatId ? { chat: chatId } : undefined,
    });
    dispatch(setAiChatOpen(true));
  }, [agentId, chatId, dispatch, live, navigate, unlocked]);

  if (!agent || !unlocked) return null;

  return (
    <AgentCard
      icon={iconRobot}
      title={agent.name}
      data-draft-agent
      trailing={
        <span className='flex items-center gap-[12px]'>
          <CardStatus>Draft</CardStatus>
          <Link
            to='/agents/$agentId'
            params={{ agentId: agent.agentId }}
            search={agent.chatId ? { chat: agent.chatId } : undefined}
            className={CARD_LINK}
          >
            Open
          </Link>
        </span>
      }
    />
  );
}
